import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';

import useWindowSize from '@/hooks/useWindowSize';

import DatePicker, { IDatePickerProps } from '../DatePicker/DatePicker';
import { VirtualizedDatePicker } from '../VirtualizedDatePicker/VirtualizedDatePicker';

interface IDatePickerModalContent extends IDatePickerProps {
  initialFrom?: Date;
}

export const DatePickerModalContent = ({
  onChange,
  disabledDays,
  disableBefore,
  dateFrom,
  dateTo,
  minimumDays,
  disableFrom,
  numberOfMonths,
  isStacked,
  initialFrom,
  isWishlist = false,
  minimumStayRequirement,
  ...rest
}: IDatePickerModalContent) => {
  const modalSpacingOffset = isWishlist ? 200 : 150;

  const [initialMonth, setInitialMonth] = useState<Date | undefined>(initialFrom);
  const { height: windowHeight } = useWindowSize();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState(600);

  const adjustedMaxHeight = windowHeight ? windowHeight * 0.9 - modalSpacingOffset : 500;

  useEffect(() => {
    if (!containerRef?.current) return;

    const observer = new ResizeObserver(([entry]) => {
      const rect = entry?.target.getBoundingClientRect();
      if (rect?.height && windowHeight) {
        const maxAllowedHeight = adjustedMaxHeight;
        const constrainedHeight = Math.min(rect.height, maxAllowedHeight);
        setContainerHeight(Math.max(300, constrainedHeight));
      }
    });

    observer.observe(containerRef.current);

    if (windowHeight) {
      const availableHeight = Math.max(300, adjustedMaxHeight);
      setContainerHeight(availableHeight);
    } else {
      setContainerHeight(600);
    }

    return () => observer.disconnect();
  }, [windowHeight, adjustedMaxHeight]);

  return !isStacked ? (
    <DatePicker
      range
      numberOfMonths={numberOfMonths}
      onChange={onChange}
      disabledDays={disabledDays}
      disableBefore={disableBefore}
      dateTo={dateTo}
      dateFrom={dateFrom}
      fromMonth={dayjs().toDate()}
      toMonth={dayjs().add(2, 'year').toDate()}
      minimumDays={minimumDays}
      month={initialMonth}
      onMonthChange={setInitialMonth}
      disableFrom={disableFrom}
      isWishlist={isWishlist}
      minimumStayRequirement={minimumStayRequirement}
      {...rest}
    />
  ) : (
    <div
      ref={containerRef}
      style={{
        height: `${adjustedMaxHeight}px`,
        minHeight: '300px',
      }}>
      <VirtualizedDatePicker
        dateFrom={dateFrom}
        dateTo={dateTo}
        onChange={onChange}
        disabledDays={disabledDays}
        disableBefore={disableBefore}
        fromMonth={dayjs().toDate()}
        minimumDays={minimumDays}
        minimumStayRequirement={minimumStayRequirement}
        totalMonths={24}
        activeMonth={initialMonth}
        onMonthChange={setInitialMonth}
        containerHeight={containerHeight}
      />
    </div>
  );
};
